import React, { useState, useEffect, useCallback } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const CheckIcon = ({ size = 10, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 10 8" fill="none" style={{ display:'inline-block', verticalAlign:'middle', flexShrink:0, ...style }}>
    <path d="M1 4L3.8 7L9 1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend-production-9457.up.railway.app';

/* ── Styles ─────────────────────────────────────────────────────────────────
   All color/shadow values reference CSS custom properties defined in
   index.css. Theme switching (dark ↔ light) is handled entirely by CSS
   via [data-theme] on documentElement — no JS re-render needed.
   ─────────────────────────────────────────────────────────────────────── */
const s = {
  wrap:          { background:'var(--color-bg)', minHeight:'100%', margin:'-40px -24px', padding:'40px 24px' },
  center:        { color:'var(--color-muted)', textAlign:'center', padding:'80px 0', fontSize:14 },
  header:        { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 },
  h1:            { fontFamily:'var(--font-display)', fontSize:28, fontWeight:700, letterSpacing:'-0.02em', color:'var(--color-text)', margin:0 },
  subtitle:      { color:'var(--color-muted)', marginTop:6, fontSize:14 },
  planBtn:       { padding:'10px 18px', background:'var(--color-magma)', color:'#fff', borderRadius:'var(--radius-lg)', textDecoration:'none', fontSize:13, fontFamily:'var(--font-display)', fontWeight:700 },
  banner:        { display:'flex', alignItems:'center', justifyContent:'space-between', background:'var(--color-accent-dim)', border:'1px solid var(--color-accent-border)', borderRadius:'var(--radius-lg)', padding:'12px 18px', marginBottom:20, gap:12 },
  bannerLeft:    { display:'flex', alignItems:'center', gap:10 },
  bannerDot:     { width:8, height:8, borderRadius:'50%', background:'var(--color-accent)', flexShrink:0, animation:'scqPulse 2s infinite' },
  bannerText:    { fontSize:14, color:'var(--color-muted)' },
  bannerClose:   { background:'none', border:'none', color:'var(--color-muted2)', cursor:'pointer', fontSize:14, padding:'2px 6px', flexShrink:0 },
  statsRow:      { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:20 },
  statCard:      { background:'var(--color-card)', border:'1px solid var(--color-border)', borderRadius:'var(--radius-lg)', padding:'20px 20px 16px', boxShadow:'var(--shadow-card)' },
  statValue:     { fontFamily:'var(--font-display)', fontSize:28, fontWeight:700, letterSpacing:'-0.03em', color:'var(--color-text)', marginBottom:4 },
  statLabel:     { fontSize:12, color:'var(--color-muted)' },
  card:          { background:'var(--color-card)', border:'1px solid var(--color-border)', borderRadius:'var(--radius-xl)', padding:24, marginBottom:20, boxShadow:'var(--shadow-card)' },
  cardHeader:    { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 },
  cardTitle:     { fontFamily:'var(--font-display)', fontSize:16, fontWeight:700, letterSpacing:'-0.01em', color:'var(--color-text)', margin:0 },
  cardDesc:      { color:'var(--color-muted)', fontSize:13, marginBottom:16, lineHeight:1.6 },
  cardLink:      { fontSize:13, color:'var(--color-accent)', textDecoration:'none', fontWeight:600 },
  cardLinkBtn:   { fontSize:12, color:'var(--color-accent)', textDecoration:'none', fontWeight:600, padding:'6px 12px', borderRadius:'var(--radius-sm)', border:'1px solid var(--color-accent-border)', background:'var(--color-accent-dim)' },
  cardMeta:      { fontSize:12, color:'var(--color-muted2)' },
  badge:         { fontSize:10, fontWeight:700, fontFamily:'var(--font-display)', letterSpacing:'0.07em', padding:'3px 9px', borderRadius:100, background:'var(--color-accent-dim)', color:'var(--color-accent)', border:'1px solid var(--color-accent-border)', textTransform:'uppercase' },
  code:          { fontFamily:'monospace', background:'var(--color-accent-dim)', padding:'1px 6px', borderRadius:4, fontSize:12, color:'var(--color-accent)' },
  recentList:    { display:'flex', flexDirection:'column', gap:12 },
  recentRow:     { display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background:'var(--color-row-bg)', borderRadius:'var(--radius-md)', border:'1px solid var(--color-border)', flexWrap:'wrap' },
  recentMeta:    { display:'flex', alignItems:'center', gap:10, minWidth:160, flex:'0 0 auto' },
  recentName:    { fontSize:13, fontWeight:600, color:'var(--color-text)', fontFamily:'var(--font-display)' },
  recentProject: { fontSize:11, color:'var(--color-accent)', background:'var(--color-accent-dim)', border:'1px solid var(--color-accent-border)', borderRadius:'var(--radius-sm)', padding:'2px 7px' },
  recentDate:    { fontSize:11, color:'var(--color-muted2)' },
  recentThemes:  { display:'flex', gap:6, flex:1, flexWrap:'wrap', alignItems:'center' },
  themeChip:     { fontSize:11, color:'var(--color-muted)', background:'var(--color-chip-bg)', border:'1px solid var(--color-border)', borderRadius:'var(--radius-sm)', padding:'3px 8px' },
  noThemes:      { fontSize:11, color:'var(--color-muted2)', fontStyle:'italic' },
  chatPill:      { marginLeft:'auto', flexShrink:0, padding:'7px 14px', background:'var(--color-accent-dim)', color:'var(--color-accent)', border:'1px solid var(--color-accent-border)', borderRadius:'var(--radius-md)', fontSize:12, fontFamily:'var(--font-display)', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' },
  recurringGrid: { display:'flex', flexWrap:'wrap', gap:8 },
  recurringChip: { display:'inline-flex', alignItems:'center', gap:8, padding:'7px 12px', background:'var(--color-chip-bg)', border:'1px solid var(--color-border)', borderRadius:'var(--radius-md)', cursor:'pointer', fontFamily:'var(--font-sans)' },
  recurringLabel:{ fontSize:13, color:'var(--color-text)' },
  recurringCount:{ fontSize:11, fontWeight:700, color:'var(--color-accent)', background:'var(--color-accent-dim)', border:'1px solid var(--color-accent-border)', borderRadius:4, padding:'1px 6px' },
  heatmapWrap:   { display:'flex', gap:6 },
  dayLabels:     { display:'flex', flexDirection:'column', justifyContent:'space-around', paddingBottom:2 },
  dayLabel:      { fontSize:10, color:'var(--color-muted2)', lineHeight:1, height:12, display:'flex', alignItems:'center' },
  heatmapGrid:   { display:'flex', gap:3, flex:1 },
  heatmapCol:    { display:'flex', flexDirection:'column', gap:3, flex:1 },
  heatCell:      { height:12, borderRadius:2, cursor:'default' },
  heatmapLegend: { display:'flex', alignItems:'center', gap:4, marginTop:10, justifyContent:'flex-end' },
  legendLabel:   { fontSize:10, color:'var(--color-muted2)' },
  legendCell:    { width:12, height:12, borderRadius:2 },
  keyRow:        { display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 },
  keyStatus:     { fontSize:14, color:'var(--color-green)', fontWeight:600 },
  copyBtn:       { padding:'11px 18px', background:'var(--color-accent-dim)', color:'var(--color-accent)', border:'1px solid var(--color-accent-border)', borderRadius:'var(--radius-lg)', fontSize:13, fontFamily:'var(--font-display)', fontWeight:700, whiteSpace:'nowrap', textDecoration:'none' },
  usageRow:      { display:'flex', alignItems:'baseline', gap:6, marginBottom:14 },
  usageNum:      { fontFamily:'var(--font-display)', fontSize:42, fontWeight:700, letterSpacing:'-0.04em', color:'var(--color-text)' },
  usageSep:      { fontSize:22, color:'var(--color-muted2)', margin:'0 4px' },
  usageTotal:    { fontSize:16, color:'var(--color-muted)' },
  progressBg:    { height:6, background:'var(--color-border)', borderRadius:3, overflow:'hidden' },
  progressFill:  { height:'100%', borderRadius:3, transition:'width 0.4s ease' },
  warning:       { marginTop:10, fontSize:13, color:'var(--color-amber)' },
  link:          { color:'var(--color-accent)' },
  empty:         { color:'var(--color-muted)', fontSize:14 },
};

const planQuota = { free:2, starter:10, growth:30, team:150 };

/* ── helpers ── */
function getThemeLabels(session) {
  if (!session.themes) return [];
  const t = Array.isArray(session.themes) ? session.themes : [];
  return t.slice(0, 3).map(th => th.label || th.name || th.title || '').filter(Boolean);
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day:'numeric', month:'short' });
}

/* ── What's new banner ── */
function WhatsNewBanner({ sessions }) {
  const LS_KEY = 'scq_last_visit';
  const lastVisit = localStorage.getItem(LS_KEY);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    localStorage.setItem(LS_KEY, new Date().toISOString());
  }, []);

  if (!lastVisit || dismissed) return null;

  const lastVisitDate = new Date(lastVisit);
  const newSessions = sessions.filter(s => s.status === 'ready' && new Date(s.created_at) > lastVisitDate);
  const newThemeCount = newSessions.reduce((acc, s) => acc + getThemeLabels(s).length, 0);
  const newQuotes = newSessions.reduce((acc, s) => acc + (s.quote_count || 0), 0);

  if (newSessions.length === 0) return null;

  return (
    <div style={s.banner}>
      <div style={s.bannerLeft}>
        <span style={s.bannerDot} />
        <span style={s.bannerText}>
          <strong style={{color:'var(--color-text)'}}>{newSessions.length} session{newSessions.length !== 1 ? 's' : ''} completed</strong>
          {' '}since your last visit
          {newThemeCount > 0 && <> · <strong style={{color:'var(--color-accent)'}}>{newThemeCount} themes</strong></>}
          {newQuotes > 0 && <> · <strong style={{color:'var(--color-accent)'}}>{newQuotes} quotes</strong> extracted</>}
        </span>
      </div>
      <button style={s.bannerClose} onClick={() => setDismissed(true)} aria-label="Dismiss">✕</button>
    </div>
  );
}

/* ── Recently completed sessions ── */
function RecentSessions({ sessions }) {
  const navigate = useNavigate();
  const ready = [...sessions]
    .filter(s => s.status === 'ready')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  if (ready.length === 0) return null;

  return (
    <div className="scq-card" style={s.card}>
      <div style={s.cardHeader}>
        <h2 style={s.cardTitle}>Recently completed</h2>
        <div style={{display:'flex', gap:10}}>
          <Link to="/sessions" style={s.cardLinkBtn}>View all sessions</Link>
          <Link to="/projects" style={s.cardLinkBtn}>View all projects</Link>
        </div>
      </div>
      <div style={s.recentList}>
        {ready.map(sess => {
          const themes = getThemeLabels(sess);
          return (
            <div key={sess.id} style={s.recentRow}>
              <div style={s.recentMeta}>
                <span style={s.recentName}>{sess.name}</span>
                {sess.project && <span style={s.recentProject}>{sess.project_name || 'Project'}</span>}
                <span style={s.recentDate}>{fmtDate(sess.created_at)}</span>
              </div>
              <div style={s.recentThemes}>
                {themes.length > 0
                  ? themes.map(t => <span key={t} style={s.themeChip}>{t}</span>)
                  : <span style={s.noThemes}>No themes yet</span>
                }
              </div>
              <button
                style={s.chatPill}
                onClick={() => navigate('/chat', { state: { sessionId: sess.id, sessionName: sess.name } })}
              >
                ✦ Explore in chat
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Recurring themes ── */
function RecurringThemes({ sessions }) {
  const navigate = useNavigate();
  const ready = sessions.filter(s => s.status === 'ready');

  // Count how many sessions each theme label appears in
  const themeMap = {};
  ready.forEach(sess => {
    const seen = new Set();
    getThemeLabels(sess).forEach(label => {
      const key = label.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        if (!themeMap[key]) themeMap[key] = { label, count: 0, sessions: [] };
        themeMap[key].count += 1;
        themeMap[key].sessions.push(sess.name);
      }
    });
  });

  const recurring = Object.values(themeMap)
    .filter(t => t.count >= 2)
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  if (recurring.length === 0) return null;

  return (
    <div className="scq-card" style={s.card}>
      <div style={s.cardHeader}>
        <h2 style={s.cardTitle}>Recurring themes</h2>
        <span style={s.badge}>Cross-study</span>
      </div>
      <p style={s.cardDesc}>Themes appearing in more than one session — your strongest signals.</p>
      <div style={s.recurringGrid}>
        {recurring.map(t => (
          <button
            key={t.label}
            style={s.recurringChip}
            onClick={() => navigate('/chat', { state: { query: `Tell me more about the theme "${t.label}" across all sessions` } })}
            title={`Appears in: ${t.sessions.join(', ')}`}
          >
            <span style={s.recurringLabel}>{t.label}</span>
            <span style={s.recurringCount}>{t.count}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Activity heatmap ── */
function ActivityHeatmap({ sessions, isLight }) {
  const WEEKS = 12;
  const DAYS  = WEEKS * 7;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Build date → count map
  const countMap = {};
  sessions.forEach(sess => {
    const d = new Date(sess.created_at);
    d.setHours(0, 0, 0, 0);
    const key = d.toISOString().slice(0, 10);
    countMap[key] = (countMap[key] || 0) + 1;
  });

  // Generate cells from oldest to newest
  const cells = [];
  for (let i = DAYS - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    cells.push({ key, count: countMap[key] || 0, date: d });
  }

  const maxCount = Math.max(...cells.map(c => c.count), 1);
  const totalUploads = cells.reduce((a, c) => a + c.count, 0);

  // Dark: dark navy → teal → cyan. Light: pale indigo → indigo.
  const SCALE = isLight
    ? ['#EEF2FF', '#C7D2FE', '#818CF8', '#4338CA']
    : ['#0C1A2E', '#0E4D62', '#0A8EA8', '#00FFFF'];

  function cellColor(count) {
    if (count === 0) return SCALE[0];
    const intensity = count / maxCount;
    if (intensity < 0.33) return SCALE[1];
    if (intensity < 0.66) return SCALE[2];
    return SCALE[3];
  }

  // Day-of-week labels: Mon=0 … Sun=6 in our grid (cells start from Mon)
  const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="scq-card" style={s.card}>
      <div style={s.cardHeader}>
        <h2 style={s.cardTitle}>Upload activity</h2>
        <span style={s.cardMeta} aria-hidden="true">Last 12 weeks</span>
      </div>
      <div
        role="img"
        aria-label={`Upload activity over the last 12 weeks. ${totalUploads} session${totalUploads !== 1 ? 's' : ''} uploaded in total.`}
        style={s.heatmapWrap}
      >
        {/* Day-of-week axis — visible labels, hidden from AT (covered by aria-label above) */}
        <div style={s.dayLabels} aria-hidden="true">
          {DAY_NAMES.map(l => (
            <span key={l} style={s.dayLabel}>{l[0]}</span>
          ))}
        </div>
        <div style={s.heatmapGrid}>
          {Array.from({ length: WEEKS }, (_, w) => (
            <div key={w} style={s.heatmapCol}>
              {cells.slice(w * 7, (w + 1) * 7).map(cell => {
                const label = `${cell.date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}: ${cell.count} session${cell.count !== 1 ? 's' : ''}`;
                return (
                  <div
                    key={cell.key}
                    role="gridcell"
                    aria-label={label}
                    tabIndex={0}
                    style={{
                      ...s.heatCell,
                      background: cellColor(cell.count),
                      cursor: cell.count > 0 ? 'help' : 'default',
                      outline: 'none',
                    }}
                    onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px var(--color-accent)'; }}
                    onBlur={e => { e.currentTarget.style.boxShadow = 'none'; }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      {/* Legend — decorative, hidden from screen readers */}
      <div style={s.heatmapLegend} aria-hidden="true">
        <span style={s.legendLabel}>Less</span>
        {SCALE.map(color => (
          <div key={color} style={{...s.legendCell, background: color}} />
        ))}
        <span style={s.legendLabel}>More</span>
      </div>
    </div>
  );
}

/* ── Main Dashboard ── */
export default function Dashboard() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [account, setAccount] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [accountRes, sessionsRes] = await Promise.all([
        fetch(`${API_BASE}/portal/me`, { headers }),
        fetch(`${API_BASE}/portal/sessions`, { headers }),
      ]);
      if (accountRes.ok) setAccount(await accountRes.json());
      if (sessionsRes.ok) { const d = await sessionsRes.json(); setSessions(d.sessions || []); }
    } catch { setError('Could not load account data.'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { loadData(); }, [loadData]);

  const quota    = planQuota[account?.plan] || 2;
  const used     = account?.sessions_this_month || 0;
  const pct      = Math.min((used / quota) * 100, 100);
  const barColor = pct > 90 ? 'var(--color-danger)' : pct > 70 ? 'var(--color-amber)' : 'var(--color-green)';

  if (loading) return <div style={s.center}>Loading your account...</div>;
  if (error)   return <div style={s.center}>{error}</div>;

  return (
    <div>
      {/* Header */}
      <div style={s.header} className="scq-page-header">
        <div>
          <h1 style={s.h1}>Welcome back{user?.firstName ? `, ${user.firstName}` : ''}</h1>
          <p style={s.subtitle}>Your research overview</p>
        </div>
        <Link to="/billing" style={s.planBtn}>
          {account?.plan === 'free' ? '↑ Upgrade plan' : `${account?.plan} plan`}
        </Link>
      </div>

      {/* What's new banner */}
      <WhatsNewBanner sessions={sessions} />

      {/* Stats row */}
      <div style={s.statsRow} className="scq-grid-4">
        {[
          { label: 'Sessions this month', value: `${used} / ${quota}` },
          { label: 'Total sessions',      value: account?.total_sessions ?? 0 },
          { label: 'Plan',                value: account?.plan || 'free', upper: true },
          { label: 'Quotes extracted',    value: sessions.reduce((a, s) => a + (s.quote_count || 0), 0) },
        ].map(({ label, value, upper }) => (
          <div key={label} style={s.statCard}>
            <div style={s.statValue}>{upper ? String(value).toUpperCase() : value}</div>
            <div style={s.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* Recently completed sessions */}
      <RecentSessions sessions={sessions} />

      {/* Recurring themes */}
      <RecurringThemes sessions={sessions} />

      {/* Usage */}
      <div className="scq-card" style={s.card}>
        <div style={s.cardHeader}>
          <h2 style={s.cardTitle}>Usage this month</h2>
          <span style={{...s.badge, background:'rgba(42,139,122,0.12)', color:'var(--color-green)', borderColor:'rgba(42,139,122,0.25)'}}>{account?.plan || 'free'}</span>
        </div>
        <div style={s.usageRow}>
          <span style={s.usageNum}>{used}</span>
          <span style={s.usageSep}>/</span>
          <span style={s.usageTotal}>{quota} sessions</span>
        </div>
        <div style={s.progressBg} role="progressbar" aria-valuenow={used} aria-valuemin={0} aria-valuemax={quota} aria-label={`${used} of ${quota} sessions used this month`}>
          <div style={{...s.progressFill, width:`${pct}%`, background: barColor}} />
        </div>
        {pct > 80 && (
          <p style={s.warning}>Running low — <Link to="/billing" style={s.link}>upgrade your plan</Link></p>
        )}
      </div>

      {/* Activity heatmap — full width */}
      <ActivityHeatmap sessions={sessions} isLight={isLight} />

      {/* API Key */}
      <div className="scq-card" style={s.card}>
        <div style={s.cardHeader}>
          <h2 style={s.cardTitle}>API Keys</h2>
          <span style={s.badge}>MCP</span>
        </div>
        <p style={s.cardDesc}>
          Add your key as <code style={s.code}>SCQ_API_KEY</code> in your Claude Desktop MCP config to connect SenseCritiq.
        </p>
        {account?.key_count > 0 ? (
          <div style={s.keyRow}>
            <span style={s.keyStatus}><CheckIcon size={11} style={{marginRight:5}} />{account.key_count} active key{account.key_count !== 1 ? 's' : ''}</span>
            <Link to="/keys" style={s.copyBtn}>Manage keys →</Link>
          </div>
        ) : (
          <div style={s.keyRow}>
            <span style={s.empty}>No API keys yet.</span>
            <Link to="/keys" style={s.copyBtn}>Create your first key →</Link>
          </div>
        )}
      </div>
    </div>
  );
}

